import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import withHeightAuto from '@activebrands/core-web/components/withHeightAuto';
import media from '@activebrands/core-web/config/media';
import useReInitWidgets from '@activebrands/core-web/libs/lipscore/hooks/useReInitWidgets';
import { styled } from '@activebrands/core-web/libs/styletron';
import getSelector from '@activebrands/core-web/utils/get-selector';
import { getFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';
import responsiveTextStyles from '@activebrands/core-web/utils/responsive-text-styles';
import PlusIcon from 'components/icons/PlusIcon';
import { AccordionContext } from './AccordionProvider';

const Wrapper = styled('div', {
    lineHeight: 1,
});

const Toggle = styled('button', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '16px',
    textAlign: 'left',
    cursor: 'pointer',
    borderTop: '1px solid var(--color-border-pdp-accordions)',
    marginTop: '-1px', // Overlap borders when closed

    [media.min['desktop.sm']]: {
        borderLeft: '1px solid var(--color-border-pdp-accordions)',
        borderRight: '1px solid var(--color-border-pdp-accordions)',
    },
    ...getFocusRingStyles(),
});

const Collapse = withHeightAuto(
    styled('div', {
        position: 'relative',
        height: '0', // Will work as default height
        overflow: 'hidden',
        transitionDuration: 'var(--duration-fast)',
        transitionProperty: 'height',
        transitionTimingFunction: 'var(--ease)',
    })
);

// Wrapping Collapse because of bug with withHeightAuto and border when trying to rerend component after lipscore has changed
const CollapseBorder = styled('div', {
    borderBottom: '1px solid var(--color-border-pdp-accordions)',

    [media.min['desktop.sm']]: {
        borderLeft: '1px solid var(--color-border-pdp-accordions)',
        borderRight: '1px solid var(--color-border-pdp-accordions)',
    },
});

const IconAndLabel = styled('div', ({ $labelTextStyle }) => ({
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    width: '100%',
    ...responsiveTextStyles(
        $labelTextStyle ?? ['Primary/16_100_-05_500', null, null, null, null, 'Primary/24_120_-1']
    ),
}));

const Icon = styled('img', {
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '24px',
});

/**
 * Use AccordionPanels as it is to render a single AccordionPanel.
 * Wrap with a AccordionProvider to enable so that only one AccordionPanel will be open at a time.
 *
 * @param {object} $style - Style-object.
 * @param {string} img - The img source url.
 * @param {string} name - The name (id) of this panel. Used by AccordionProvider.
 * @param {string} label - The label shown when the panel is closed. Can be updated with setAccordionLabel.
 * @param {bool} isLastChild - If true, will add a borderBottom.
 * @param {string | (string | null)[]} labelTextStyle - Optional font key(s) to style the accordion label.
 * @param {func} render - Render-function used to render children.
 * @param {any} rest - Will be passed to children inside the render-function.
 */

const AccordionPanel = ({
    $style = {},
    id = '',
    img = '',
    isLipscore = false,
    label = '',
    labelTextStyle,
    name = '',
    render = () => null,
    selector,
    ...rest
}) => {
    const context = useContext(AccordionContext);
    const [isOpen, setIsOpen] = useState(context ? context.contains(name) : false);
    const [clicked, setClicked] = useState(false);

    useEffect(() => {
        if (context) {
            setIsOpen(context.contains(name));
        }
    }, [context]);

    const toggle = () => {
        if (context) {
            context.toggle(name);
        } else {
            setIsOpen(!isOpen);
        }
    };

    const checkHash = rawHash => {
        const hash = rawHash?.replace('#', '');
        if (hash === id) {
            toggle();
        }
    };

    const checkName = eventDetailName => {
        if (eventDetailName === name) {
            toggle();
        }
    };

    const handleEvent = eventDetails => {
        const { name, hash } = eventDetails || {};

        if (name) {
            checkName(name);
        } else if (hash) {
            checkHash(hash);
        }
    };

    useEffect(() => {
        window.addEventListener('scrollTo', event => handleEvent(event.detail), false);
        if (window.location.hash) {
            checkHash(window.location.hash);
        }
        return () => {
            window.removeEventListener('scrollTo', event => handleEvent(event.detail), false);
        };
    }, []);

    // ReInit lipscore when toggling review accordion
    if (isLipscore) {
        useReInitWidgets(isOpen);
    }

    const handleCollapseClick = () => {
        // Only lipscore reviews needs rerendering
        if (isLipscore) {
            setClicked(true);
        }
    };

    useEffect(() => {
        if (clicked) {
            // Delay rerender until lipscore has new height after using filters
            const timeoutId = setTimeout(() => {
                setClicked(false);
            }, 200);

            return () => clearTimeout(timeoutId);
        }
    }, [clicked]);

    return (
        <Wrapper data-selector={selector} $style={$style} id={id}>
            <Toggle
                aria-expanded={isOpen}
                data-selector={getSelector(selector, 'header')}
                type="button"
                onClick={toggle}
            >
                <IconAndLabel
                    data-selector={getSelector(selector, 'title')}
                    $labelTextStyle={labelTextStyle}
                    $style={{ paddingLeft: img ? '36px' : '' }}
                >
                    {img && <Icon src={img} />}
                    {label}
                </IconAndLabel>
                <PlusIcon
                    data-selector={getSelector(selector, 'action')}
                    $style={{
                        width: ['16px', null, null, null, null, '24px'],
                        transition: 'transform var(--transition-fast)',
                        transform: isOpen ? 'rotate(45deg)' : 'none',
                    }}
                />
            </Toggle>
            <CollapseBorder>
                <Collapse
                    data-selector={getSelector(selector, 'content')}
                    className="accordion-panel-collapse"
                    height={isOpen ? 'auto' : 0}
                    inert={!isOpen ? '' : undefined}
                    onClick={handleCollapseClick}
                >
                    {render({ ...rest })}
                </Collapse>
            </CollapseBorder>
        </Wrapper>
    );
};

AccordionPanel.propTypes = {
    $style: PropTypes.object,
    id: PropTypes.string,
    img: PropTypes.string,
    isLipscore: PropTypes.bool,
    label: PropTypes.string.isRequired,
    labelTextStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    name: PropTypes.string.isRequired,
    render: PropTypes.func.isRequired,
    selector: PropTypes.string,
};

export default AccordionPanel;
